import { useCallback, useState } from 'react';
import { trim } from 'lodash';
import { useNavigate } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import {
    addNewCollectionIdentifiers,
    setCollectionsRecords,
} from '../../data-layer/collections/actions';
import * as collectionsService from '../../data-layer/collections/service';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { getPostsSuggests } from '../../data-layer/posts/service';
import { useDispatch } from '../../data-layer/useDispatch';
import { confirmAlert } from '../../ui/feedback/Alert';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../ui/feedback/Dialog';
import { useDialogScroll } from '../../ui/feedback/hooks/useDialogScroll';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { CollectionInfoContent } from '../../ui/users/CollectionInfoContent/CollectionInfoContent';
import { CpaTrialDisclaimer } from '../../ui/users/CpaTrialDisclaimer/CpaTrialDisclaimer';
import { useCpaTrial } from '../../ui/users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { useCreateCollectionSessionStorage } from '../../ui/users/CreateCollectionDialog/hooks/useCreateCollectionSessionStorage';
import { ActionButton } from '../../ui/users/styles';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../utils/LocationContext';
import { EventSource } from '../../utils/analyticsService';
import { getIsMobile } from '../../utils/getIsMobile';
import { useTranslation } from '../../utils/useTranslation';
import { useCollectionInfo } from './hooks/useCollectionInfo';

const CLOSE_DRAWER_TIMEOUT = 500;

export const CreateCollectionFromPostsDialog = () => {
    // Данные из useDialogSteps записываются в history.state.usr
    const historyState = window.history.state?.usr;
    const postIds = (historyState?.createCollectionData?.postIds ?? []) as string[];
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { clearValue } = useCreateCollectionSessionStorage();
    const isMobile = getIsMobile().isMobile;

    const onClose = useCallback(() => {
        clearValue();
        navigate(-1);
    }, [clearValue, navigate]);

    const onCloseGuard = useCallback(() => {
        confirmAlert(
            'Вы уверены, что хотите закрыть окно?',
            'Все несохраненные изменения будут потеряны.',
        )
            .then(() => {
                onClose();
            })
            .catch(() => {});
    }, [onClose]);

    const t = useTranslation();
    const { modalContentRef, atTop, atBottom } = useDialogScroll();
    const { eventSource } = useEventSource();
    const screen = eventSource ?? EventSource.POST;

    const { isNotRegistered, refreshUserProfile } = useCpaTrial();

    const { values, handlers } = useCollectionInfo({
        initialValues: {
            numberOfPosts: postIds.length,
            displayName: '',
            isPublic: true,
        },
        id: postIds[0],
        getSuggestMethod: () => getPostsSuggests(postIds),
        needLoadSuggests: true,
        screen,
        mode: 'create',
    });

    const [isSaving, setIsSaving] = useState(false);

    const onSave = authenticationService.protect(async () => {
        if (isSaving) {
            return;
        }

        try {
            setIsSaving(true);
            const response = await collectionsService.saveToNewCollection({
                postIds,
                collectionName: values.showInFeed ? values.currentSuggest : values.collectionName,
                isPublic: values.isPublic,
                showInFeed: values.showInFeed,
                needCpaRegistration: isNotRegistered,
            });
            dispatch(setCollectionsRecords(...response.records.collections));
            dispatch(
                addNewCollectionIdentifiers({
                    namespace: 'default',
                    identifiers: response.collections,
                }),
            );

            const collection = response.records.collections[0];
            const collectionId = response.collections[0];

            contentActionsService.trackCollectionCreated({
                id: collectionId,
                postIds,
                screen,
                newValues: {
                    displayName: collection.displayName,
                    showInFeed: collection.feedStatus?.showInFeed,
                    isPublic: collection.isPublic,
                },
            });

            if (isNotRegistered) {
                refreshUserProfile();
            }

            onClose();
            setTimeout(() => {
                navigate(`/collections/${collectionId}`);
            }, CLOSE_DRAWER_TIMEOUT);
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось сохранить пост в коллекцию'));
        } finally {
            setIsSaving(false);
        }
    });

    const submitDisabled =
        isSaving ||
        !trim(
            values.showInFeed && values.currentSuggest
                ? values.currentSuggest
                : values.collectionName,
        );

    if (!postIds) {
        return null;
    }

    return (
        <Dialog onClose={onCloseGuard}>
            <DialogTitle onClose={onCloseGuard} withBorderBottom={!atTop}>
                <Typography variant="title3" weight="bold">
                    {t('Новая коллекция')}
                </Typography>
            </DialogTitle>
            <DialogContent ref={modalContentRef} py={0} px={0}>
                <CollectionInfoContent
                    values={values}
                    handlers={{ ...handlers, onSubmit: onSave }}
                />
            </DialogContent>
            <DialogFooter withBorderTop={!atBottom}>
                <Flex direction="column" width="100%" pt={isMobile ? 1 : 0}>
                    <ActionButton
                        dataTestId={DataTestId.CREATE_COLLECTION_SUBMIT}
                        minHeight="46px"
                        onClick={onSave}
                        disabled={submitDisabled}
                    >
                        {t('Создать')}
                    </ActionButton>
                    {isNotRegistered ? <CpaTrialDisclaimer /> : null}
                </Flex>
            </DialogFooter>
        </Dialog>
    );
};
